import { amplitudeService } from '../services/amplitude';

/**
 * Check if Amplitude event tracking is enabled (only when fully enabled)
 * When only partially enabled, only user properties are sent, no event tracking
 */
const isAmplitudeTrackingEnabled = (): boolean => {
  const amplitudeConfig = window.BFG?.amplitude || window.BF?.amplitude;
  // Only allow event tracking when fully enabled
  return !!amplitudeConfig?.apiKey && !!amplitudeConfig?.enabled;
};

/**
 * Initialize Amplitude with configuration from global BFG object
 */
export const initializeAmplitude = (): void => {

  // Check if Amplitude is enabled and configured
  // Use BFG.amplitude if available, otherwise fallback to BF.amplitude (for login page)
  const amplitudeConfig = window.BFG?.amplitude || window.BF?.amplitude;
  const isFullyEnabled = !!amplitudeConfig?.enabled; // This comes from amplitude_fully_enabled feature flag
  const isPartiallyEnabled = !!window.BFG.hasFeature?.('amplitude_surveys_enabled');
  const shouldInitialize = isFullyEnabled || isPartiallyEnabled;

  // Initialize if either fully or partially enabled, and API key exists
  if (!shouldInitialize || !amplitudeConfig?.apiKey) {
    return;
  }

  // Initialize Amplitude
  amplitudeService.init(amplitudeConfig.apiKey);

  // Set user if available (not available on login page)
  // Only include sensitive user data when amplitude_surveys_enabled is true
  if (window.BFG?.currentUser?.user_id && isPartiallyEnabled) {
    const userProperties = {
      email: window.BFG.currentUser.email,
      company: window.BFG.currentUser.company,
      role: window.BFG.currentUser.role,
      device: window.BFG.currentUser.device,
      environment: amplitudeConfig.environment,
      // Plan feature flags for targeting
      has_people_tagging: !!window.BFG.hasFeature?.('people_tagging'),
      // Brandfolder-level settings (if in a brandfolder context)
      brandfolder_people_tagging_enabled: !!window.BFG.brandfolderSettings?.people_tagging,
    };

    // List of properties to unset that we don't want to send
    const propertiesToUnset = [
      'has_auto_tagging',
      'has_combined_role_limits',
      'has_custom_permissions',
      'has_resource_level_limit',
      'has_smart_cdn',
      'brandfolder_auto_tagging_enabled',
      'organization_key',
      'organization_name',
      // Note: referrer, referring_domain, and initial_* properties are automatically
      // collected by Amplitude SDK and cannot be unset
    ];

    amplitudeService.setUser(window.BFG.currentUser.user_id, userProperties, propertiesToUnset);
  }

  // Expose amplitudeService globally for inline scripts
  window.amplitudeService = amplitudeService;
};

/**
 * Track user action
 */
export const trackUserAction = (action: string, properties?: Record<string, any>): void => {
  if (isAmplitudeTrackingEnabled()) {
    amplitudeService.trackUserAction(action, properties);
  }
};

/**
 * Track asset interaction
 */
export const trackAssetInteraction = (action: string, assetId: string, properties?: Record<string, any>): void => {
  if (isAmplitudeTrackingEnabled()) {
    amplitudeService.trackAssetInteraction(action, assetId, properties);
  }
};
